import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCampaign } from "../context/CampaignContext";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

const businessTypes = [
  { id: "realestate", icon: "🏢", label: "Real Estate" },
  { id: "local",      icon: "🛍️", label: "Local Service Business" },
  { id: "ecommerce",  icon: "📦", label: "E-commerce" },
  { id: "coach",      icon: "🎓", label: "Coach / Consultant" },
  { id: "creator",    icon: "🎥", label: "Content Creator" },
  { id: "app",        icon: "📱", label: "App / Software" },
];

const goals = [
  { id: "leads",     icon: "🎯", label: "Get Leads" },
  { id: "calls",     icon: "📞", label: "Get Phone Calls" },
  { id: "sales",     icon: "🛒", label: "Drive Sales" },
  { id: "traffic",   icon: "🌐", label: "Drive Website Traffic" },
  { id: "youtube",   icon: "▶️", label: "Promote a YouTube Video" },
  { id: "awareness", icon: "📣", label: "Increase Brand Awareness" },
];

const experience = [
  { id: "new",    label: "I am completely new",   desc: "Never run ads before" },
  { id: "little", label: "I know a little",       desc: "Tried but need guidance" },
  { id: "before", label: "I have run ads before", desc: "Looking to improve results" },
];

export default function Screen1() {
  const navigate = useNavigate();
  const { updateSelections } = useCampaign();
  const { user, userName } = useAuth();
  const [selected, setSelected] = useState({ business: "", goal: "", experience: "" });

  const toggle = (key, val) =>
    setSelected(s => ({ ...s, [key]: s[key] === val ? "" : val }));

  const allAnswered = selected.business && selected.goal && selected.experience;

  return (
    <div style={s.page}>
      {/* Nav */}
      <nav style={s.nav}>
        <Logo size={36} showText={true} textSize={17} />
        <button style={s.signInLink} onClick={() => navigate(user ? "/dashboard" : "/signin")}>
          {user ? `👋 ${userName}` : "Sign In"}
        </button>
      </nav>

      <div style={s.container}>
        {/* Step dots */}
        <div style={s.stepRow}>
          {[1,2,3,4,5,6,7].map(n => (
            <div key={n} style={{ ...s.stepDot, background: n === 1 ? "#6366f1" : "#e2e8f0" }} />
          ))}
        </div>
        <p style={s.stepLabel}>Step 1 of 7</p>

        {/* Hero */}
        <h1 style={s.headline}>
          Launch Your First Google Ads Campaign{" "}
          <span style={s.highlight}>Without Guesswork</span>
        </h1>
        <p style={s.subtext}>
          A step-by-step guided system for beginners, business owners, and service
          providers who want to run Google Ads with clarity and confidence.
        </p>
        <div style={s.trustBadge}>
          <span>🛡️</span>
          <span style={s.trustText}>
            No experience needed. No confusing setup. Just simple guided steps to help you launch with confidence.
          </span>
        </div>

        {/* Q1 */}
        <div style={s.card}>
          <div style={s.qHeader}>
            <span style={s.qNum}>01</span>
            <div>
              <div style={s.qTitle}>What type of business do you run?</div>
              <div style={s.qSub}>Choose the option that best describes your business.</div>
            </div>
          </div>
          <div style={s.chipGrid}>
            {businessTypes.map(b => (
              <button key={b.id} style={{
                ...s.chip,
                borderColor: selected.business === b.id ? "#6366f1" : "#e2e8f0",
                background:  selected.business === b.id ? "linear-gradient(135deg,#eef2ff,#f0f9ff)" : "#fafafa",
                color:       selected.business === b.id ? "#4f46e5" : "#334155",
                fontWeight:  selected.business === b.id ? 700 : 500,
              }} onClick={() => toggle("business", b.id)}>
                <span style={{ fontSize: 22 }}>{b.icon}</span>
                {b.label}
                {selected.business === b.id && <span style={s.tick}>✓</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Q2 */}
        <div style={s.card}>
          <div style={s.qHeader}>
            <span style={s.qNum}>02</span>
            <div>
              <div style={s.qTitle}>What is your main goal?</div>
              <div style={s.qSub}>Pick the result you want most from your campaign.</div>
            </div>
          </div>
          <div style={s.rowList}>
            {goals.map(g => (
              <button key={g.id} style={{
                ...s.rowItem,
                borderColor: selected.goal === g.id ? "#6366f1" : "#e2e8f0",
                background:  selected.goal === g.id ? "#eef2ff" : "#fafafa",
              }} onClick={() => toggle("goal", g.id)}>
                <span style={{ fontSize: 20 }}>{g.icon}</span>
                <span style={{ ...s.rowLabel, color: selected.goal === g.id ? "#4f46e5" : "#334155", fontWeight: selected.goal === g.id ? 700 : 500 }}>
                  {g.label}
                </span>
                <div style={{ width:18, height:18, borderRadius:"50%", border:`2px solid ${selected.goal === g.id ? "#6366f1" : "#cbd5e1"}`, background: selected.goal === g.id ? "#6366f1" : "transparent", flexShrink:0 }} />
              </button>
            ))}
          </div>
        </div>

        {/* Q3 */}
        <div style={s.card}>
          <div style={s.qHeader}>
            <span style={s.qNum}>03</span>
            <div>
              <div style={s.qTitle}>How much experience do you have with Google Ads?</div>
              <div style={s.qSub}>This helps us guide you at the right level.</div>
            </div>
          </div>
          <div style={s.rowList}>
            {experience.map(e => (
              <button key={e.id} style={{
                ...s.expItem,
                borderColor: selected.experience === e.id ? "#6366f1" : "#e2e8f0",
                background:  selected.experience === e.id ? "linear-gradient(135deg,#eef2ff,#f0f9ff)" : "#fafafa",
              }} onClick={() => toggle("experience", e.id)}>
                <div>
                  <div style={{ fontSize:14, fontWeight:600, color: selected.experience === e.id ? "#4f46e5" : "#1e293b", fontFamily:"sans-serif" }}>{e.label}</div>
                  <div style={{ fontSize:12, color:"#94a3b8", fontFamily:"sans-serif" }}>{e.desc}</div>
                </div>
                {selected.experience === e.id && (
                  <div style={{ width:24, height:24, borderRadius:"50%", background:"#6366f1", color:"#fff", fontSize:12, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"sans-serif" }}>✓</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{ marginTop: 8, paddingBottom: 48 }}>
          {allAnswered && (
            <p style={{ fontSize:13, color:"#16a34a", fontFamily:"sans-serif", textAlign:"center", marginBottom:14, fontWeight:600 }}>
              ✅ We'll use your answers to guide your next steps.
            </p>
          )}
          <button
            style={{ ...s.primaryBtn, opacity: allAnswered ? 1 : 0.4, cursor: allAnswered ? "pointer" : "not-allowed" }}
            onClick={() => {
              if (!allAnswered) return;
              updateSelections({
                businessType: selected.business,
                goal:         selected.goal,
                experience:   selected.experience,
              });
              navigate("/free-setup");
            }}
          >
            Continue →
          </button>
          <p style={{ textAlign:"center", fontSize:13, color:"#94a3b8", fontFamily:"sans-serif", marginTop:16 }}>
            Already have an account?{" "}
            <span style={{ color:"#6366f1", fontWeight:600, cursor:"pointer" }} onClick={() => navigate("/signin")}>Sign in</span>
          </p>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:      { minHeight:"100vh", background:"linear-gradient(160deg,#f8faff 0%,#eef2ff 60%,#f0fdf4 100%)", fontFamily:"'Georgia',serif", paddingBottom:60 },
  nav:       { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 32px", background:"#fff", borderBottom:"1px solid #e2e8f0", boxShadow:"0 1px 6px rgba(0,0,0,0.05)", position:"sticky", top:0, zIndex:100 },
  logo:      { display:"flex", alignItems:"center", gap:10 },
  logoMark:  { width:34, height:34, borderRadius:9, background:"linear-gradient(135deg,#6366f1,#818cf8)", color:"#fff", fontWeight:800, fontSize:16, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"sans-serif" },
  logoText:  { fontSize:17, fontWeight:800, color:"#1e293b", fontFamily:"sans-serif", letterSpacing:"-0.3px" },
  signInLink:{ fontSize:14, fontWeight:600, color:"#6366f1", background:"none", border:"none", cursor:"pointer", fontFamily:"sans-serif" },
  container: { maxWidth:600, margin:"0 auto", padding:"32px 20px 0" },
  stepRow:   { display:"flex", gap:6, marginBottom:8 },
  stepDot:   { height:4, flex:1, borderRadius:99 },
  stepLabel: { fontSize:12, color:"#94a3b8", fontFamily:"sans-serif", marginBottom:24, marginTop:0 },
  headline:  { fontSize:28, fontWeight:700, color:"#0f172a", lineHeight:1.25, margin:"0 0 14px" },
  highlight: { background:"linear-gradient(135deg,#6366f1,#06b6d4)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" },
  subtext:   { fontSize:15, color:"#475569", lineHeight:1.65, margin:"0 0 18px", fontFamily:"sans-serif" },
  trustBadge:{ display:"flex", gap:10, padding:"14px 18px", background:"#f0fdf4", borderRadius:12, border:"1px solid #bbf7d0", marginBottom:28 },
  trustText: { fontSize:13, color:"#166534", lineHeight:1.5, fontFamily:"sans-serif" },
  card:      { background:"#fff", borderRadius:18, border:"1px solid #f1f5f9", boxShadow:"0 2px 20px rgba(99,102,241,0.06)", padding:"24px 22px", marginBottom:18 },
  qHeader:   { display:"flex", gap:14, alignItems:"flex-start", marginBottom:18 },
  qNum:      { fontSize:13, fontWeight:800, color:"#6366f1", background:"#eef2ff", borderRadius:8, padding:"4px 9px", fontFamily:"sans-serif", flexShrink:0 },
  qTitle:    { fontSize:16, fontWeight:700, color:"#1e293b", marginBottom:4, fontFamily:"sans-serif" },
  qSub:      { fontSize:13, color:"#64748b", fontFamily:"sans-serif" },
  chipGrid:  { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 },
  chip:      { display:"flex", flexDirection:"column", alignItems:"center", gap:6, padding:"14px 10px", borderRadius:12, border:"1.5px solid", cursor:"pointer", fontSize:13, fontFamily:"sans-serif", transition:"all 0.2s", position:"relative" },
  tick:      { position:"absolute", top:6, right:8, fontSize:10, color:"#6366f1", fontWeight:800 },
  rowList:   { display:"flex", flexDirection:"column", gap:10 },
  rowItem:   { display:"flex", alignItems:"center", gap:12, padding:"13px 16px", borderRadius:12, border:"1.5px solid", cursor:"pointer", transition:"all 0.2s" },
  rowLabel:  { fontSize:14, fontFamily:"sans-serif", flex:1, textAlign:"left" },
  expItem:   { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"15px 18px", borderRadius:12, border:"1.5px solid", cursor:"pointer", transition:"all 0.2s", textAlign:"left" },
  primaryBtn:{ width:"100%", padding:"16px", borderRadius:12, background:"linear-gradient(135deg,#6366f1,#818cf8)", color:"#fff", border:"none", fontSize:16, fontWeight:700, cursor:"pointer", fontFamily:"sans-serif", boxShadow:"0 4px 18px rgba(99,102,241,0.35)" },
};